"use client";
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import ProtectedRoute from '@/components/ProtectedRoute';
import Sidebar from '@/components/ui/Sidebar/Sidebar';
import Dot from '@/components/ui/Dot';
import { useAuth } from '@/contexts/AuthContext';
import { useUnifiedPopup } from '@/components/ui/UnifiedPopup/UnifiedPopup';
import { FeedFilter, FeedType } from '@/components/ui/FeedFilter';
import DotsBalance from '@/components/ui/DotsBalance/DotsBalance';
import { useDotsBalance } from '@/hooks/useDotsBalance';
import CongratulationPopup from '@/components/ui/PopUp/CongratulationPopup';
import { Notifications } from '@/components/ui/Notification';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '@/services/api';
import { hasUnreadLocalNotifications } from '@/utils/communityWelcomeNotification';
import { mergeJoinRequestOutcome } from '@shared/utils/joinRequestResolutionStorage';
import { TermsUpdateModal } from '@/components/ui/TermsUpdateModal/TermsUpdateModal';
import { useAppTour } from '@/contexts/AppTourContext';

interface AppLayoutProps {
  children: React.ReactNode;
  showBalance?: boolean;
  showSidebar?: boolean;
  showFloatingButton?: boolean;
  showFeedFilter?: boolean;
  selectedFeed?: FeedType;
  onFeedChange?: (feed: FeedType) => void;
  maxWidth?: string;
  className?: string;
  /**
   * Main column does not scroll; children fill remaining height (flex + min-h-0).
   * Use with a page that applies overflow-y-auto only to its own content pane.
   */
  nestedScrollLayout?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  showBalance = true,
  showSidebar = true,
  showFloatingButton = true,
  showFeedFilter = false,
  selectedFeed = "for you",
  onFeedChange,
  maxWidth = "max-w-[620px]",
  className = "",
  nestedScrollLayout = false,
}) => {
  const { user, token, refreshUser } = useAuth();
  const { openPopup, closePopup } = useUnifiedPopup();
  // App tour: while the "send" step is active, lift the floating send button above
  // the tour backdrop (visible but not clickable) like the highlighted mobile tab icon.
  const { currentStep: tourStep } = useAppTour();
  const tourHighlightsSend = tourStep?.highlightTab === 'send';
  const { tasks, daysLeft, loading, error, refreshData, showCongratulationPopup, congratulationData, closeCongratulationPopup } = useDotsBalance(token);

  // Mobile notification state
  const [showMobileNotificationDrawer, setShowMobileNotificationDrawer] = useState(false);
  const mobileNotificationRef = useRef<HTMLDivElement>(null);
  const [hasUnreadLocal, setHasUnreadLocal] = useState(false);
  const hasUnreadNotifications = (user?.hasUnreadNotifications ?? false) || hasUnreadLocal;

  // Client-only notifications: show pink dot when unread
  useEffect(() => {
    setHasUnreadLocal(hasUnreadLocalNotifications(user?.uid));
    const onAdded = () => setHasUnreadLocal(true);
    const onRead = () => setHasUnreadLocal(hasUnreadLocalNotifications(user?.uid));
    window.addEventListener('joydrop-community-welcome-added', onAdded);
    window.addEventListener('joydrop-community-awaiting-approval-added', onAdded);
    window.addEventListener('joydrop-community-notice-added', onAdded);
    window.addEventListener('joydrop-local-welcome-read', onRead);
    window.addEventListener('joydrop-local-awaiting-read', onRead);
    window.addEventListener('joydrop-local-community-notice-read', onRead);
    return () => {
      window.removeEventListener('joydrop-community-welcome-added', onAdded);
      window.removeEventListener('joydrop-community-awaiting-approval-added', onAdded);
      window.removeEventListener('joydrop-community-notice-added', onAdded);
      window.removeEventListener('joydrop-local-welcome-read', onRead);
      window.removeEventListener('joydrop-local-awaiting-read', onRead);
      window.removeEventListener('joydrop-local-community-notice-read', onRead);
    };
  }, [user?.uid]);

  /** Persist join-request outcomes when the notifications drawer is unmounted so reopening still shows settled rows. */
  useEffect(() => {
    if (!user?.uid) return;
    const handler = (e: Event) => {
      const d = (e as CustomEvent<{
        joinRequestId?: string;
        outcome?: 'approved' | 'denied';
      }>).detail;
      if (!d?.joinRequestId || (d.outcome !== 'approved' && d.outcome !== 'denied')) return;
      mergeJoinRequestOutcome(user.uid, d.joinRequestId, d.outcome);
    };
    window.addEventListener('joydrop-pending-join-resolved', handler);
    return () => window.removeEventListener('joydrop-pending-join-resolved', handler);
  }, [user?.uid]);

  // Mobile header scroll state
  const [isMobileHeaderVisible, setIsMobileHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const mainRef = useRef<HTMLElement>(null);


  // Close mobile notification drawer when clicking outside
  useEffect(() => {
    if (!showMobileNotificationDrawer) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (mobileNotificationRef.current && !mobileNotificationRef.current.contains(event.target as Node)) {
        setShowMobileNotificationDrawer(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMobileNotificationDrawer]);

  // Handle scroll for mobile header collapse
  useEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement) return;

    const handleScroll = () => {
      const currentScrollY = mainElement.scrollTop;
      const scrollDifference = currentScrollY - lastScrollY.current;

      // Only hide/show if scrolled more than 5px to avoid flickering
      if (Math.abs(scrollDifference) > 5) {
        if (scrollDifference > 0 && currentScrollY > 50) {
          // Scrolling down - hide header
          setIsMobileHeaderVisible(false);
        } else if (scrollDifference < 0) {
          // Scrolling up - show header
          setIsMobileHeaderVisible(true);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    mainElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mainElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMobileNotificationClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const nextState = !showMobileNotificationDrawer;
    setShowMobileNotificationDrawer(nextState);

    // Mark server notifications as read when drawer opens; local welcome are marked when user views community tab
    if (nextState && user?.uid && token) {
      try {
        await apiService.markUserNotificationsAsRead(user.uid, token);
        await refreshUser();
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
      }
    }
  };

  const handleJoydropClick = () => {
    openPopup({
      id: 'sendJoydrop',
      type: 'sendJoydrop',
      position: 'center',
      size: '2xl',
      closeOnOverlayClick: true,
      props: {
        onSuccess: () => {
          console.log('Joydrop sent successfully');
        },
        onError: (error: Error) => {
          console.error('Failed to send joydrop:', error);
        }
      },
      zIndex: 200,
    });
  };

  const handleBalanceClick = () => {
    openPopup({
      id: 'dotsBalance',
      type: 'custom',
      position: 'center',
      size: 'lg',
      closeOnOverlayClick: true,
      props: {
        content: (
          <DotsBalance
            balance={user?.dots || 0}
            daysLeft={daysLeft}
            tasks={tasks}
            onClose={() => closePopup('dotsBalance')}
            loading={loading}
            error={error}
            onRefresh={refreshData}
          />
        ),
      },
      zIndex: 200,
    });
  };

  return (
    <ProtectedRoute>
      <div className="flex h-screen">
        {/* Sidebar */}
        {showSidebar && <Sidebar />}

        <main
          ref={mainRef}
          className={`relative flex-1 bg-[#F6F2EC] overflow-x-visible ${
            nestedScrollLayout
              ? "flex min-h-0 flex-col overflow-y-hidden"
              : "overflow-y-auto"
          }`}
        >
          {/* Stars Background */}
          {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="fixed top-0 w-full h-full">
              <Stars />
            </div>
          </div> */}

          {/* Mobile Header - Logo and Notification - Visible only on mobile */}
          <div
            className={`md:hidden fixed top-0 left-0 right-0 bg-[#F6F2EC] z-30 transition-transform duration-300 ease-in-out ${isMobileHeaderVisible ? 'translate-y-0' : '-translate-y-full'
              }`}
          >
            <div className="flex items-center justify-between px-4 py-3">
              {/* Logo */}
              <Link href="/" className="ml-2">
                <span className="fraunces-display cursor-pointer text-xl tracking-tight text-[#151515] lowercase sm:text-2xl">
                  joydrop
                </span>
              </Link>

              {/* Notification Icon */}
              <button
                onClick={handleMobileNotificationClick}
                className="relative p-2"
                aria-label="Notifications"
              >
                <Image
                  src="/notifications.svg"
                  alt="Notifications"
                  width={24}
                  height={24}
                  className="w-6 h-6 flex-shrink-0"
                />
                {hasUnreadNotifications && !showMobileNotificationDrawer && (
                  <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-[#FF5BAE]" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Notification Drawer */}
          <AnimatePresence>
            {showMobileNotificationDrawer && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40 md:hidden"
                  style={{
                    backgroundColor: 'rgba(48, 22, 92, 0.6)',
                  }}
                  onClick={() => setShowMobileNotificationDrawer(false)}
                />

                {/* Drawer */}
                <motion.div
                  ref={mobileNotificationRef}
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 220, mass: 0.8 }}
                  className="fixed top-0 right-0 w-full h-full bg-[#F9F4F2] z-50 shadow-2xl md:hidden"
                >
                  <Notifications
                    onNavigate={() => setShowMobileNotificationDrawer(false)}
                    onOpenSearch={(opts) => {
                      setShowMobileNotificationDrawer(false);
                      window.dispatchEvent(
                        new CustomEvent("joydrop-open-search-drawer", {
                          detail: { tab: opts?.tab },
                        })
                      );
                    }}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <div
            className={`relative z-10 pb-20 md:pb-0 pt-8 md:pt-0 ${className} ${
              nestedScrollLayout
                ? "flex min-h-0 flex-1 flex-col"
                : "min-h-screen"
            }`}
          >

            <div
              className={`mx-auto w-full px-4 py-8 ${maxWidth} ${
                nestedScrollLayout
                  ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                  : ""
              }`}
            >
              {/* Header Section with Feed Filter and Balance */}
              <div
                className={`flex w-full shrink-0 flex-row ${showFeedFilter ? 'justify-between' : 'justify-end'}`}
              >
                {/* Feed Filter - Only show when enabled */}
                {showFeedFilter && onFeedChange && (
                  <div className="mt-2">
                    <FeedFilter
                      selectedFeed={selectedFeed}
                      onFeedChange={onFeedChange}
                    />
                  </div>
                )}

                {/* Balance Display - Always show at right when enabled */}
                {showBalance && (
                  <div className="flex justify-end items-center gap-3 mb-6">
                    {/* Marketplace shortcut, to the left of the dots pill
                        instead of being buried inside the balance popup.
                        Bare icon, no pill/button background — the source
                        SVG's strokes are hardcoded white, so brightness-0
                        renders it solid black to stay visible against the
                        light header. */}
                    <Link
                      href="/market"
                      aria-label="Go to Marketplace"
                      className="flex-shrink-0 md:hidden flex items-center justify-center w-10 h-10 rounded-full border-2 border-black bg-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-gray-800 transition-all active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
                    >
                      {/* The source SVG's strokes are hardcoded white, so on
                          this purple background it renders correctly as-is —
                          no color filter/mask needed. */}
                      <Image
                        src="/SideBar/VectorMarket.svg"
                        alt="Marketplace"
                        width={26}
                        height={26}
                        className="w-[26px] h-[26px]"
                      />
                    </Link>
                    <div
                      className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={handleBalanceClick}
                    >
                      <Dot dots={user?.dots || 0} id={1} />
                    </div>
                  </div>
                )}
              </div>

              {/* Page Content */}
              {children}
            </div>
          </div>

          {/* Floating Send Joydrop Button - Hidden on mobile, shown on md and above */}
          {showFloatingButton && (
            <button
              onClick={handleJoydropClick}
              className={`hidden md:flex fixed bottom-[40px] right-[40px] w-[88px] h-[88px]
                 bg-[#F9F4F2] rounded-full border-2 border-black items-center justify-center hover:bg-[#F2ECE9] transition-colors shadow-md ${
                   tourHighlightsSend ? 'z-[10001] pointer-events-none' : 'z-20'
                 }`}
            >
              <Image
                src="/send-jd-icon-black.svg"
                alt="Send joydrop"
                width={32}
                height={32}
                className="w-6 h-6 sm:w-8 sm:h-8"
              />
            </button>
          )}
        </main>
      </div>

      {/* Congratulation Popup */}
      {showCongratulationPopup && (
        <CongratulationPopup
          popupType={congratulationData.popupType as 'joydropBonus' | 'joiningBonus' | 'streakBonus'}
          balance={congratulationData.dots}
          onBack={closeCongratulationPopup}
        />
      )}
      <TermsUpdateModal />
    </ProtectedRoute>
  );
};

export default AppLayout;
